import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  bulkAddEntitlementGroupMembers,
  confirmEntitlementGroupChange,
  confirmEntitlementMembership,
  createEntitlementGroup,
  deleteEntitlementGroup,
  fetchEntitlementAllowanceTotals,
  fetchEntitlementGroupAllowanceTotal,
  fetchEntitlementGroupMembers,
  fetchEntitlementGroups,
  previewEntitlementGroupChange,
  previewEntitlementMembership,
  renameEntitlementGroup,
  setEntitlementGroupOwner,
  type BulkMembershipOutcome,
  type EntitlementAllowanceTotals,
  type EntitlementGroupMember,
  type EntitlementGroupSummary,
  type GroupChange,
  type GroupImpactPreview,
} from "../../api/entitlementGroupsAdmin";
import { createAssociationValue, deleteAssociationValue, fetchAssociationCatalogue, fetchAssociationUsers, fetchPeople, renameAssociationValue, setAssociationOwner, type AssociationValue, type ManagedAssociationKind, type UnitOwnerResponse } from "../../api/entitlementAdmin";
import type { AdminResult } from "../../api/adminApi";
import { AdminGate } from "../../components/admin/AdminGate";
import { DetailRow, EntitlementDetailPanel } from "../../components/admin/EntitlementDetailPanel";
import { Modal } from "../../components/admin/Modal";
import type { PersonSummary as SearchPersonSummary } from "../../api/session";
import { SearchSelect, type SearchSelectOutcome } from "../../components/shared/SearchSelect";
import { BackstageLogo } from "../../components/shared/BackstageIcon";
import { BulkAssociationSection } from "../../components/admin/BulkAssociationSection";
import { ErrorState, LoadingState } from "../../components/states";
import { useToast } from "../../components/toast/toastContext";
import { useSession } from "../../session/SessionContext";
import styles from "../../components/admin/Admin.module.css";
import pageStyles from "./EntitlementGroupsAdminPage.module.css";
import { OrganisationImportDialog } from "./OrganisationImportDialog";
import { Dropdown, type DropdownOption } from "../../components/shared/Dropdown";
import { fetchEntitlementBudgets, type BudgetUnit } from "../../api/entitlementBudgets";
import { fetchOrganisationBudgets } from "../../api/organisationReporting";
import { BudgetCellContent, CapexCellContent, ConsumedBudgetCellContent, type BudgetSpend } from "../../components/budgets/BudgetCellContent";
import { BudgetEditor } from "../../components/budgets/BudgetEditor";
import { BudgetHistory, GroupAllowanceHistory } from "../../components/budgets/BudgetHistory";
import { budgetKey } from "../../components/budgets/budgetFormat";
import { currentPeriodStart } from "../../hooks/useSelectedPeriod";
import { Tooltip } from "../../components/shared/Tooltip";

const permission = "groups.administer";
const organisationImportPermission = "organisation.import";
const entitlementAdministerPermission = "entitlement.administer";
const globalScopePermission = "scope.global";
// Budgets: the spend against them needs consumption.read.org, and a back-dated budget change needs budgets.backdate (GH-135, R-16).
const organisationConsumptionPermission = "consumption.read.org";
const budgetBackdatePermission = "budgets.backdate";

// The key an editor and its history are remounted on: a change to the stored budget or hard stop starts them again from the saved values.
const budgetEditorKey = (unit: BudgetUnit): string => `${unit.kind}:${unit.id}:${String(unit.explicitBudgetUsd)}:${String(unit.ownHardStop)}:${String(unit.effectiveHardStop)}`;
// Whole US dollars, as the other admin and request pages show money.
const wholeDollars = new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 });

// Flattens the three lists into one map from row id to total. Ids are unique across groups, Departments and Divisions.
function totalsById(totals: EntitlementAllowanceTotals): Map<string, number | null> {
  return new Map([...totals.groups, ...totals.departments, ...totals.divisions].map((row): [string, number | null] => [row.id, row.totalAllowanceUsd]));
}

export function EntitlementGroupsAdminPage(): React.JSX.Element {
  const { t } = useTranslation();
  const { snapshot } = useSession();
  const toast = useToast();
  const canImportOrganisation = snapshot.status === "signed-in" && snapshot.permissions.includes(organisationImportPermission);
  // Divisions and the bulk assignment of users to a Department are entitlement.administer operations on the server, so they are shown only to holders of it.
  const canAdministerEntitlement = snapshot.status === "signed-in" && snapshot.permissions.includes(entitlementAdministerPermission);
  // Without Allow Global Scope the tree holds only the groups the caller owns, so only the Departments and Divisions above them are shown.
  const canSeeAllGroups = snapshot.status === "signed-in" && snapshot.permissions.includes(globalScopePermission);
  // The Budget column shows this month's spend only to a caller who may see the organisation's spend; a back-dated change only to a holder of budgets.backdate.
  const canSeeSpend = snapshot.status === "signed-in" && snapshot.permissions.includes(organisationConsumptionPermission);
  const canBackdate = snapshot.status === "signed-in" && snapshot.permissions.includes(budgetBackdatePermission);
  // Every unit's budget, keyed by kind and id, and this month's spend against it for a caller who may see the spend.
  const [budgets, setBudgets] = useState<Map<string, BudgetUnit>>(() => new Map());
  const [budgetSpend, setBudgetSpend] = useState<Map<string, BudgetSpend>>(() => new Map());
  const [groups, setGroups] = useState<EntitlementGroupSummary[]>([]);
  const [divisionCatalogue, setDivisions] = useState<AssociationValue[]>([]);
  const [departmentCatalogue, setDepartments] = useState<AssociationValue[]>([]);
  // Without Allow Global Scope a Department or Division is shown only when it holds a group in the tree, so a caller sees the structure of their own groups and no more.
  const { divisions, departments } = useMemo(() => {
    if (canSeeAllGroups) return { divisions: divisionCatalogue, departments: departmentCatalogue };
    const usedDepartments = new Set(groups.map((group) => group.departmentId));
    const shownDepartments = departmentCatalogue.filter((department) => usedDepartments.has(department.id));
    const usedDivisions = new Set(shownDepartments.map((department) => department.parentId));
    return { divisions: divisionCatalogue.filter((division) => usedDivisions.has(division.id)), departments: shownDepartments };
  }, [canSeeAllGroups, divisionCatalogue, departmentCatalogue, groups]);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [selectedStructure, setSelectedStructure] = useState<AssociationValue | null>(null);
  // Mirrors selectedStructure for load(), which must not auto-select a group while a Division or Department is selected.
  const selectedStructureRef = useRef<AssociationValue | null>(null);
  const [members, setMembers] = useState<EntitlementGroupMember[]>([]);
  const [loading, setLoading] = useState(true);
  const [membersLoading, setMembersLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [preview, setPreview] = useState<(GroupImpactPreview & GroupChange) | null>(null);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  // The unit a new group is added under: the Department or group whose Add group button was clicked.
  const [createParentId, setCreateParentId] = useState<string | null>(null);
  // The unit whose Delete button is waiting for its second click; deleting is two clicks so a slip cannot remove a unit.
  const [deleteConfirmId, setDeleteConfirmId] = useState<string | null>(null);
  // What the dialog creates: a group (under a Department or group) or a Department (under a Division).
  const [createKind, setCreateKind] = useState<"Group" | "Department">("Group");
  const openCreate = (kind: "Group" | "Department", parentId: string) => { setCreateKind(kind); setCreateParentId(parentId); setCreateDialogOpen(true); };
  const [importDialogOpen, setImportDialogOpen] = useState(false);
  const [divisionDialogOpen, setDivisionDialogOpen] = useState(false);
  const [assignUsersOpen, setAssignUsersOpen] = useState(false);
  // Bumped after a bulk assignment, so the Department's user count and email list are fetched again.
  const [structureUsersRevision, setStructureUsersRevision] = useState(0);
  const [bulkDialogOpen, setBulkDialogOpen] = useState(false);
  const [exportDialogOpen, setExportDialogOpen] = useState(false);
  // The distinct people of the selected Division or Department, tagged with the structure they were fetched for.
  const [structureUsers, setStructureUsers] = useState<{ id: string; count: number; directCount: number; emails: string[]; totalAllowanceUsd: number | null } | null>(null);
  const [structureEmailsOpen, setStructureEmailsOpen] = useState(false);
  const [collapsedGroupIds, setCollapsedGroupIds] = useState<Set<string>>(() => new Set());
  // The allowance of every group, Department and Division from one bulk call, keyed by row id. A row without a total is shown empty.
  const [allowanceTotals, setAllowanceTotals] = useState<Map<string, number | null>>(() => new Map());
  const allowanceRequest = useRef(0);
  const [draftParent, setDraftParent] = useState("");
  const [draftAllowance, setDraftAllowance] = useState("");
  const [draftGroupId, setDraftGroupId] = useState<string | null>(null);
  const [editingName, setEditingName] = useState(false);
  const treePanelRef = useRef<HTMLElement>(null);
  const [treeBounds, setTreeBounds] = useState<{ top: number; maxHeight: number } | null>(null);

  const load = useCallback(async (showLoading = true) => {
    if (showLoading) setLoading(true);
    setError(null);
    // One bulk call for every row's allowance. A response that is no longer the latest is ignored; a failed call leaves the column empty, silently.
    const allowanceRequestId = allowanceRequest.current + 1;
    allowanceRequest.current = allowanceRequestId;
    void fetchEntitlementAllowanceTotals().then((allowance) => {
      if (allowanceRequest.current !== allowanceRequestId) return;
      setAllowanceTotals(allowance.kind === "ok" ? totalsById(allowance.response) : new Map());
    });
    const [result, divisionResult, departmentResult, budgetResult, spendResult] = await Promise.all([
      fetchEntitlementGroups(true),
      fetchAssociationCatalogue("Division"),
      fetchAssociationCatalogue("Department"),
      fetchEntitlementBudgets(),
      canSeeSpend ? fetchOrganisationBudgets(currentPeriodStart()) : Promise.resolve(null),
    ]);
    // A budget that did not load leaves its cell empty, silently, as the allowance column does.
    setBudgets(new Map(budgetResult.kind === "ok" && Array.isArray(budgetResult.response) ? budgetResult.response.map((unit): [string, BudgetUnit] => [budgetKey(unit.kind, unit.id), unit]) : []));
    setBudgetSpend(new Map(spendResult?.kind === "ok" && Array.isArray(spendResult.response.units)
      ? spendResult.response.units.map((unit): [string, BudgetSpend] => [budgetKey(unit.kind, unit.id), { spendUsd: unit.countedSpendUsd, overBudget: unit.overBudget }])
      : []));
    if (divisionResult.kind === "ok") setDivisions(divisionResult.response);
    if (departmentResult.kind === "ok") setDepartments(departmentResult.response);
    // A saved owner changes the Division or Department the panel shows, so the selected one is re-read from the fresh catalogue.
    const selectedNode = selectedStructureRef.current;
    if (selectedNode) {
      const catalogue = selectedNode.kind === "Division" ? divisionResult : departmentResult;
      const fresh = catalogue.kind === "ok" ? catalogue.response.find((node) => node.id === selectedNode.id) : undefined;
      if (fresh && (fresh.managerEmail !== selectedNode.managerEmail || fresh.value !== selectedNode.value)) {
        selectedStructureRef.current = fresh;
        setSelectedStructure(fresh);
      }
    }
    if (result.kind === "ok") {
      setGroups(result.response);
      setSelectedId((current) => current && result.response.some((group) => group.id === current) ? current : selectedStructureRef.current ? null : result.response.find((group) => !group.archivedAt)?.id ?? null);
    } else {
      setError(result.kind === "denied" ? t("groupAdmin.denied") : result.message);
    }
    if (showLoading) setLoading(false);
  }, [t, canSeeSpend]);

  useEffect(() => { void load(); }, [load]);

  useEffect(() => {
    const measure = () => {
      const panel = treePanelRef.current;
      const grid = panel?.parentElement;
      const main = panel?.closest("main");
      if (!grid || !main) return;
      const mainRect = main.getBoundingClientRect();
      const offset = grid.getBoundingClientRect().top - mainRect.top + main.scrollTop;
      let below = 0;
      for (let element: HTMLElement | null = grid; element && element !== main; element = element.parentElement) {
        const style = getComputedStyle(element);
        below += parseFloat(style.paddingBottom) + parseFloat(style.borderBottomWidth) + parseFloat(style.marginBottom);
      }
      const mainStyle = getComputedStyle(main);
      below += parseFloat(mainStyle.paddingBottom) + parseFloat(mainStyle.borderBottomWidth);
      const navHeight = main.querySelector("nav")?.getBoundingClientRect().height ?? 0;
      setTreeBounds({ top: navHeight, maxHeight: Math.max(main.clientHeight - offset - below, 160) });
    };
    measure();
    window.addEventListener("resize", measure);
    return () => window.removeEventListener("resize", measure);
  }, [loading]);

  const selectGroup = (id: string) => {
    selectedStructureRef.current = null;
    setSelectedStructure(null);
    setSelectedId(id);
    setNotice(null);
  };
  const selectStructure = (node: AssociationValue) => {
    selectedStructureRef.current = node;
    setSelectedStructure(node);
    setSelectedId(null);
    setPreview(null);
    setNotice(null);
  };
  useEffect(() => {
    setStructureUsers(null);
    setStructureEmailsOpen(false);
    if (!selectedStructure) return;
    let cancelled = false;
    void fetchAssociationUsers(selectedStructure.kind as ManagedAssociationKind, selectedStructure.id).then((result) => {
      // A failed lookup leaves the count and the email button out rather than showing an inline error.
      if (!cancelled && result.kind === "ok") setStructureUsers({ id: selectedStructure.id, count: result.response.count, directCount: result.response.directCount, emails: result.response.users.map((user) => user.email), totalAllowanceUsd: result.response.totalAllowanceUsd });
    });
    return () => { cancelled = true; };
  }, [selectedStructure, structureUsersRevision]);
  const structureUserCount = structureUsers?.id === selectedStructure?.id ? structureUsers?.count ?? null : null;
  const structureDirectCount = structureUsers?.id === selectedStructure?.id ? structureUsers?.directCount ?? null : null;
  const structureTotalAllowanceUsd = structureUsers?.id === selectedStructure?.id ? structureUsers?.totalAllowanceUsd ?? null : null;
  const selected = groups.find((group) => group.id === selectedId) ?? null;
  // The total allowance of the selected group, tagged with the group it was fetched for. Refetched when the members change so it follows membership edits; a failed lookup shows nothing.
  const [groupAllowance, setGroupAllowance] = useState<{ id: string; totalAllowanceUsd: number | null } | null>(null);
  useEffect(() => {
    if (!selectedId || groups.find((group) => group.id === selectedId)?.canManage === false) return;
    let cancelled = false;
    void fetchEntitlementGroupAllowanceTotal(selectedId).then((result) => {
      if (!cancelled && result.kind === "ok") setGroupAllowance({ id: selectedId, totalAllowanceUsd: result.response.totalAllowanceUsd });
    });
    return () => { cancelled = true; };
  }, [selectedId, members, groups]);
  const groupTotalAllowanceUsd = groupAllowance?.id === selected?.id ? groupAllowance?.totalAllowanceUsd ?? null : null;
  const selectedAllowance = selected?.monthlyAllowanceUsd === null ? "" : String(selected?.monthlyAllowanceUsd ?? "");
  const groupChangeDirty = selected !== null && draftGroupId === selected.id && (draftParent !== (selected.parentId ?? selected.departmentId ?? "") || draftAllowance !== selectedAllowance);
  const loadMembers = useCallback(async (groupId: string) => {
    setMembersLoading(true);
    const result = await fetchEntitlementGroupMembers(groupId);
    if (result.kind === "ok") setMembers(result.response);
    else setError(result.kind === "denied" ? t("groupAdmin.denied") : result.message);
    setMembersLoading(false);
  }, [t]);

  useEffect(() => {
    setPreview(null);
    const group = groups.find((item) => item.id === selectedId) ?? null;
    if (group) {
      setDraftParent(group.parentId ?? group.departmentId ?? "");
      setDraftAllowance(group.monthlyAllowanceUsd === null ? "" : String(group.monthlyAllowanceUsd));
      setDraftGroupId(group.id);
      setEditingName(false);
      if (group.canManage === false) {
        setMembers([]);
      } else {
        void loadMembers(group.id);
      }
    } else {
      setMembers([]);
    }
  }, [selectedId, groups, loadMembers]);

  // A Division or Department can be deleted only when nothing is assigned to it and nothing sits under it: a Division has no Departments, and a Department has no group, archived or not. Imported units never can be.
  const structureHasChildren = selectedStructure?.kind === "Division"
    ? departments.some((department) => department.parentId === selectedStructure.id)
    : groups.some((group) => group.departmentId === selectedStructure?.id);
  const structureDeletable = Boolean(selectedStructure) && !selectedStructure?.backstageName && structureUserCount === 0 && !structureHasChildren;
  // A group can be deleted only when it is live, has no active member and no child group (archived or not), and was not imported.
  const groupDeletable = Boolean(selected) && !selected?.archivedAt && !selected?.backstageName && selected?.directMemberCount === 0 && !groups.some((group) => group.parentId === selected?.id);
  const renderDelete = (unitId: string, onConfirm: () => Promise<void>): React.JSX.Element => deleteConfirmId === unitId
    ? <>
      <button type="button" className="button button-secondary" onClick={() => setDeleteConfirmId(null)} disabled={busy}>{t("groupAdmin.cancel")}</button>
      <button type="button" className="button button-primary" onClick={() => void onConfirm()} disabled={busy}>{t("groupAdmin.confirmDelete")}</button>
    </>
    : <button type="button" className="button button-secondary" onClick={() => setDeleteConfirmId(unitId)} disabled={busy}>{t("groupAdmin.delete")}</button>;
  const panelStyle = treeBounds ? ({ "--tree-top": `${treeBounds.top}px`, "--tree-max-height": `${treeBounds.maxHeight}px` } as React.CSSProperties) : undefined;
  // The budget of the selected Division, Department or group, when the budget tree holds it.
  const structureBudget = selectedStructure ? budgets.get(budgetKey(selectedStructure.kind, selectedStructure.id)) ?? null : null;
  const selectedBudget = selected ? budgets.get(budgetKey("Group", selected.id)) ?? null : null;
  // reloadGroupId is the group whose members are shown once the mutation has landed. It defaults to the selection as rendered, which a
  // caller that changes the selection must override: the closure still holds the group it was called for.
  const reportMutation = async (result: { kind: string; message?: string }, success: string, reloadGroupId: string | null = selectedId) => {
    setBusy(false);
    setPreview(null);
    if (result.kind === "ok") {
      setNotice(success);
      await load(false);
      if (reloadGroupId) await loadMembers(reloadGroupId);
      return;
    }
    // A refusal is a toast, and the hierarchy stays on screen: a page-level error would replace it, which a refused group move (GH-135, R-9) must not do.
    toast.show(result.kind === "denied" ? t("groupAdmin.denied") : result.message ?? t("groupAdmin.failed"), "error");
    if (result.kind === "conflict") toast.show(t("groupAdmin.conflictReloaded"), "error");
    else if (result.kind === "error") toast.show(t("groupAdmin.auditMayHaveCommitted"), "error");
    await load(false);
    if (reloadGroupId) await loadMembers(reloadGroupId);
  };

  // After a delete the selection is cleared, so the panel does not show a unit that no longer exists.
  const clearSelection = () => {
    selectedStructureRef.current = null;
    setSelectedStructure(null);
    setSelectedId(null);
    setDeleteConfirmId(null);
  };

  // After a delete the selection moves to the unit the group sat under, a parent group or else its Department, rather than to the deleted group.
  const deleteGroup = async () => {
    if (!selected) return;
    const parentGroupId = selected.parentId;
    const department = departments.find((candidate) => candidate.id === selected.departmentId);
    setBusy(true); setError(null);
    const result = await deleteEntitlementGroup(selected.id, selected.revision);
    if (result.kind === "ok") {
      clearSelection();
      if (parentGroupId) selectGroup(parentGroupId);
      else if (department) selectStructure(department);
    }
    await reportMutation(result, t("groupAdmin.deleted"), parentGroupId);
  };

  const deleteStructure = async () => {
    if (!selectedStructure) return;
    setBusy(true); setError(null);
    const result = await deleteAssociationValue(selectedStructure.kind as ManagedAssociationKind, selectedStructure.id);
    if (result.kind === "ok") clearSelection();
    await reportMutation(result, t(selectedStructure.kind === "Division" ? "groupAdmin.divisionDeleted" : "groupAdmin.departmentDeleted"));
  };

  const create = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = new FormData(event.currentTarget);
    setBusy(true); setError(null);
    const textValue = (key: string) => { const value = form.get(key); return typeof value === "string" ? value : ""; };
    const name = textValue("name").trim();
    const parentId = createParentId;
    if (parentId === null) {
      setBusy(false); setError(t("groupAdmin.parentRequired")); return;
    }
    if (createKind === "Department") {
      const result = await createAssociationValue("Department", name, parentId);
      if (result.kind === "ok") {
        setCreateDialogOpen(false);
        selectStructure(result.response);
      }
      await reportMutation(result, t("groupAdmin.departmentCreated"));
      return;
    }
    const allowanceText = textValue("allowance");
    const amount = allowanceText === "" ? null : Number(allowanceText);
    if (amount !== null && (!Number.isInteger(amount) || amount < 0 || amount > 99999)) {
      setBusy(false); setError(t("groupAdmin.invalidAllowance")); return;
    }
    const result = await createEntitlementGroup(name, parentId, amount);
    if (result.kind === "ok") {
      setCreateDialogOpen(false);
      selectGroup(result.response.id);
    }
    await reportMutation(result, t("groupAdmin.created"));
  };

  // A Division is top level, so it takes only a name. Departments and groups are added under a selected parent instead.
  const createDivision = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const value = new FormData(event.currentTarget).get("name");
    const name = (typeof value === "string" ? value : "").trim();
    setBusy(true); setError(null);
    const result = await createAssociationValue("Division", name);
    if (result.kind === "ok") {
      setDivisionDialogOpen(false);
      selectStructure(result.response);
    }
    await reportMutation(result, t("groupAdmin.divisionCreated"));
  };

  const rename = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!selected) return;
    const value = new FormData(event.currentTarget).get("rename");
    const name = (typeof value === "string" ? value : "").trim();
    if (!name) { setError(t("groupAdmin.nameRequired")); return; }
    setBusy(true); setError(null);
    const result = await renameEntitlementGroup(selected.id, name, selected.revision);
    await reportMutation(result, t("groupAdmin.saved"));
    if (result.kind === "ok") setEditingName(false);
  };

  // Saves the parent and allowance drafts as a preview. Called from the Save button and from Enter in the allowance field.
  const requestPreview = async () => {
    if (!selected) return;
    const parentId = draftParent || null;
    const amount = draftAllowance.trim() === "" ? null : Number(draftAllowance);
    if (amount !== null && (!Number.isInteger(amount) || amount < 0 || amount > 99999)) {
      toast.show(t("groupAdmin.invalidAllowance"), "error"); return;
    }
    const change: GroupChange = { parentId, monthlyAllowanceUsd: amount };
    setError(null); setNotice(null); setBusy(true); setPreview(null);
    const result = await previewEntitlementGroupChange(selected.id, change);
    setBusy(false);
    if (result.kind === "ok") setPreview({ ...result.response, parentId: change.parentId, monthlyAllowanceUsd: change.monthlyAllowanceUsd });
    else {
      // A refused move, such as one that breaks a Division's budget (GH-135, R-9), shows its reason in a toast.
      toast.show(result.kind === "denied" ? t("groupAdmin.denied") : result.message, "error");
      if (result.kind === "conflict") toast.show(t("groupAdmin.conflictReloaded"), "error");
    }
  };

  const confirmChange = async () => {
    if (!selected || !preview) return;
    const snapshot = preview;
    setBusy(true); setError(null); setPreview(null);
    const result = await confirmEntitlementGroupChange(
      selected.id,
      { parentId: snapshot.parentId, monthlyAllowanceUsd: snapshot.monthlyAllowanceUsd },
      snapshot,
    );
    await reportMutation(result, t("groupAdmin.saved"));
  };

  const tree = useMemo(() => groups.filter((group) => !group.archivedAt), [groups]);
  // Each group's members plus those of every group beneath it. A person in two of these groups is counted once per group.
  const totalMembersByGroup = useMemo(() => {
    const childrenOf = new Map<string, EntitlementGroupSummary[]>();
    for (const group of tree) {
      if (group.parentId) childrenOf.set(group.parentId, [...(childrenOf.get(group.parentId) ?? []), group]);
    }
    const totals = new Map<string, number>();
    const totalFor = (group: EntitlementGroupSummary): number => {
      const known = totals.get(group.id);
      if (known !== undefined) return known;
      const total = group.directMemberCount + (childrenOf.get(group.id) ?? []).reduce((sum, child) => sum + totalFor(child), 0);
      totals.set(group.id, total);
      return total;
    };
    for (const group of tree) totalFor(group);
    return totals;
  }, [tree]);
  // The allowance a group with none of its own shows as a watermark: the value the effective rule (EffectiveAllowanceCalculator) would apply
  // to a member of it. That rule walks up the group parents and takes the largest allowance on the path, so this does too. It never
  // reads a Department: a Department carries no allowance, and the rule does not inherit from one.
  const getInheritedAllowance = (group: EntitlementGroupSummary): number | null => {
    let inherited: number | null = null;
    let parentId = group.parentId;
    const visited = new Set([group.id]);
    while (parentId !== null && !visited.has(parentId)) {
      visited.add(parentId);
      const parent = tree.find((candidate) => candidate.id === parentId);
      if (!parent) break;
      if (parent.monthlyAllowanceUsd !== null && (inherited === null || parent.monthlyAllowanceUsd > inherited)) inherited = parent.monthlyAllowanceUsd;
      parentId = parent.parentId;
    }
    return inherited;
  };
  const departmentPath = (department: AssociationValue): string => {
    const division = divisions.find((candidate) => candidate.id === department.parentId);
    return division ? `${division.value} / ${department.value}` : department.value;
  };
  // The path of a group from its top-most ancestor down to itself, as the parent options show it.
  const groupPath = (group: EntitlementGroupSummary): string => {
    const names = [group.name];
    let parentId = group.parentId;
    const visited = new Set([group.id]);
    while (parentId !== null && !visited.has(parentId)) {
      visited.add(parentId);
      const parent = tree.find((candidate) => candidate.id === parentId);
      if (!parent) break;
      names.unshift(parent.name);
      parentId = parent.parentId;
    }
    return names.join(" / ");
  };
  // The parent of a group as text, for an imported group whose parent cannot be changed in the portal.
  const parentText = (group: EntitlementGroupSummary): string => {
    if (group.parentId) {
      const parent = tree.find((candidate) => candidate.id === group.parentId);
      return parent ? groupPath(parent) : "";
    }
    const department = departments.find((candidate) => candidate.id === group.departmentId);
    return department ? departmentPath(department) : "";
  };
  const parentOptions = (excludeId?: string): DropdownOption[] => {
    const groupNames = (group: EntitlementGroupSummary): { names: string; departmentId: string | null } => {
      const names = [group.name];
      let current: EntitlementGroupSummary | undefined = group;
      while (current?.parentId) {
        current = tree.find((candidate) => candidate.id === current?.parentId);
        if (current) names.unshift(current.name);
      }
      return { names: names.join(" / "), departmentId: current?.departmentId ?? group.departmentId };
    };
    const groupsByDepartment = new Map<string | null, { id: string; label: string }[]>();
    for (const group of tree.filter((candidate) => candidate.id !== excludeId)) {
      const { names, departmentId } = groupNames(group);
      const key = departmentId !== null && departments.some((department) => department.id === departmentId) ? departmentId : null;
      groupsByDepartment.set(key, [...(groupsByDepartment.get(key) ?? []), { id: group.id, label: names }]);
    }
    const byLabel = (left: { label: string }, right: { label: string }) => left.label.localeCompare(right.label);
    // Each department is a heading over its own entry and the groups beneath it; the groups with no department come last, under no heading.
    const toOptions = (items: { id: string; label: string }[], group?: string): DropdownOption[] =>
      items.sort(byLabel).map((option) => ({ value: option.id, label: option.label, group }));
    return [
      ...departments
        .map((department) => ({ department, label: departmentPath(department) }))
        .sort(byLabel)
        .flatMap(({ department, label }) => [
          { value: department.id, label: department.value, group: label },
          ...toOptions(groupsByDepartment.get(department.id) ?? [], label),
        ]),
      ...toOptions(groupsByDepartment.get(null) ?? []),
    ];
  };
  const toggleCollapsed = (id: string) => setCollapsedGroupIds((current) => {
    const next = new Set(current);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    return next;
  });
  const expandAll = () => setCollapsedGroupIds(new Set());
  const collapseAll = () => setCollapsedGroupIds(new Set([
    ...divisions.filter((division) => departments.some((department) => department.parentId === division.id)).map((division) => division.id),
    ...departments.filter((department) => tree.some((group) => group.departmentId === department.id && group.parentId === null)).map((department) => department.id),
    ...tree.filter((group) => tree.some((child) => child.parentId === group.id)).map((group) => group.id),
  ]));
  const stripedRows = (() => {
    const striped = new Map<string, boolean>();
    let index = 0;
    const mark = (id: string) => { striped.set(id, index % 2 === 1); index += 1; };
    const childGroups = (parentId: string | null) => tree.filter((group) => (parentId !== null && departments.some((department) => department.id === parentId) ? group.parentId === null && group.departmentId === parentId : group.parentId === parentId && (parentId !== null || group.departmentId === null)));
    const walkGroups = (parentId: string | null) => {
      for (const group of childGroups(parentId)) {
        mark(group.id);
        if (!collapsedGroupIds.has(group.id)) walkGroups(group.id);
      }
    };
    for (const division of divisions) {
      mark(division.id);
      if (collapsedGroupIds.has(division.id)) continue;
      for (const department of departments.filter((candidate) => candidate.parentId === division.id)) {
        mark(department.id);
        if (!collapsedGroupIds.has(department.id)) walkGroups(department.id);
      }
    }
    walkGroups(null);
    return striped;
  })();
  // A department's members are those of the top-level groups filed under it, and of every group beneath them. A division's
  // are those of its departments. Both are sums of the group totals, so they are always the same figure as the groups.
  const departmentMemberTotal = (departmentId: string): number => tree
    .filter((group) => group.departmentId === departmentId && group.parentId === null)
    .reduce((sum, group) => sum + (totalMembersByGroup.get(group.id) ?? 0), 0);
  const divisionMemberTotal = (divisionId: string): number => departments
    .filter((department) => department.parentId === divisionId)
    .reduce((sum, department) => sum + departmentMemberTotal(department.id), 0);
  // The allowance of a row: empty when the row has no active members beneath it, and "$0" only when it has members who contribute nothing.
  const totalAllowanceText = (id: string): string | null => {
    const total = allowanceTotals.get(id);
    return total === null || total === undefined ? null : wholeDollars.format(total);
  };
  // The Capex cell for a unit, the Budget cell beside it (its budget and hard stop) and the Consumed budget cell (the spend counted against it this month, only for a caller who may see it).
  const budgetCell = (kind: string, id: string): React.JSX.Element => (
    <>
      <span className={`${pageStyles.valueCell} ${pageStyles.colCapex}`}>
        <CapexCellContent unit={budgets.get(budgetKey(kind, id))} />
      </span>
      <span className={`${pageStyles.valueCell} ${pageStyles.colBudget}`}>
        <BudgetCellContent unit={budgets.get(budgetKey(kind, id))} />
      </span>
      {canSeeSpend && (
        <span className={`${pageStyles.valueCell} ${pageStyles.colConsumed}`}>
          <ConsumedBudgetCellContent unit={budgets.get(budgetKey(kind, id))} spend={budgetSpend.get(budgetKey(kind, id)) ?? null} />
        </span>
      )}
    </>
  );
  // Nesting indents the name, never the value columns, so every row's value cells sit at the same x position at every depth.
  const treeIndent = (depth: number): React.JSX.Element => (
    <span className={pageStyles.treeIndent} style={{ "--depth": String(depth) } as React.CSSProperties} aria-hidden="true" />
  );
  // The same value columns a group row has (total, direct, monthly allowance, total allowance), so every row's values land in the same place.
  const renderStructuralSlots = (node: AssociationValue): React.JSX.Element => {
    const total = node.kind === "Department" ? departmentMemberTotal(node.id) : divisionMemberTotal(node.id);
    return (
      <>
        <span className={`${pageStyles.valueCell} ${pageStyles.colTotal}`} aria-hidden="true">{total > 0 && <Tooltip label={t("groupAdmin.structuralMemberBadgeTitle", { count: total })}><span className={pageStyles.totalMemberBadge}>{total}</span></Tooltip>}</span>
        <span className={`${pageStyles.valueCell} ${pageStyles.colDirect}`} aria-hidden="true" />
        <span className={`${pageStyles.valueCell} ${pageStyles.colAllowance}`} aria-hidden="true" />
        <span className={`${pageStyles.valueCell} ${pageStyles.colTotalAllowance}`}>{totalAllowanceText(node.id)}</span>
        {budgetCell(node.kind, node.id)}
      </>
    );
  };
  const renderStructuralNode = (node: AssociationValue, children: React.JSX.Element | null, depth: number): React.JSX.Element => (
    <li key={node.id}>
      <div className={`${pageStyles.treeRow} ${stripedRows.get(node.id) ? pageStyles.treeRowAlt : ""}`}>
        {treeIndent(depth)}
        {children ? <button type="button" className={pageStyles.disclosureButton} aria-label={t(collapsedGroupIds.has(node.id) ? "groupAdmin.expand" : "groupAdmin.collapse", { name: node.value })} aria-expanded={!collapsedGroupIds.has(node.id)} onClick={() => toggleCollapsed(node.id)}><span aria-hidden="true">{collapsedGroupIds.has(node.id) ? "▸" : "▾"}</span></button> : <span className={pageStyles.disclosureSpacer} aria-hidden="true" />}
        <button type="button" aria-pressed={node.id === selectedStructure?.id} className={node.id === selectedStructure?.id ? pageStyles.selectedGroup : pageStyles.groupButton} data-kind={node.kind} onClick={() => selectStructure(node)}><span className={pageStyles.groupName}>{node.value}</span>{renderStructuralSlots(node)}</button>
      </div>
      {children && !collapsedGroupIds.has(node.id) && children}
    </li>
  );
  const renderRoot = (): React.JSX.Element => (
    <ul className={pageStyles.treeList} aria-label={t("groupAdmin.hierarchy")}>
      {divisions.map((division) => {
        const divisionDepartments = departments.filter((department) => department.parentId === division.id);
        return renderStructuralNode(division, divisionDepartments.length === 0 ? null : (
          <ul className={pageStyles.treeChildren}>
            {divisionDepartments.map((department) => renderStructuralNode(department, tree.some((group) => group.departmentId === department.id && group.parentId === null) ? renderTree(department.id, 2) : null, 1))}
          </ul>
        ), 0);
      })}
      {tree.some((group) => group.parentId === null && group.departmentId === null) && <li>{renderTree(null, 0)}</li>}
    </ul>
  );
  const renderTree = (parentId: string | null, depth: number): React.JSX.Element => (
    <ul className={parentId === null ? pageStyles.treeList : pageStyles.treeChildren} aria-label={parentId === null ? t("groupAdmin.hierarchy") : undefined}>
      {tree.filter((group) => (parentId !== null && departments.some((department) => department.id === parentId) ? group.parentId === null && group.departmentId === parentId : group.parentId === parentId && (parentId !== null || group.departmentId === null))).map((group) => (
        <li key={group.id}>
          <div className={`${pageStyles.treeRow} ${stripedRows.get(group.id) ? pageStyles.treeRowAlt : ""}`}>
            {treeIndent(depth)}
            {tree.some((child) => child.parentId === group.id) ? <button type="button" className={pageStyles.disclosureButton} aria-label={t(collapsedGroupIds.has(group.id) ? "groupAdmin.expand" : "groupAdmin.collapse", { name: group.name })} aria-expanded={!collapsedGroupIds.has(group.id)} onClick={() => toggleCollapsed(group.id)}><span aria-hidden="true">{collapsedGroupIds.has(group.id) ? "▸" : "▾"}</span></button> : <span className={pageStyles.disclosureSpacer} aria-hidden="true" />}
            <button type="button" aria-pressed={group.id === selectedId} className={group.id === selectedId ? pageStyles.selectedGroup : pageStyles.groupButton} onClick={() => selectGroup(group.id)}>
              <span className={pageStyles.groupName}>{group.name}</span>
              <span className={`${pageStyles.valueCell} ${pageStyles.colTotal}`} aria-hidden="true">{tree.some((child) => child.parentId === group.id) && (totalMembersByGroup.get(group.id) ?? 0) > 0 && <Tooltip label={t("groupAdmin.totalMemberBadgeTitle", { count: totalMembersByGroup.get(group.id) ?? 0 })}><span className={pageStyles.totalMemberBadge}>{totalMembersByGroup.get(group.id) ?? 0}</span></Tooltip>}</span>
              <span className={`${pageStyles.valueCell} ${pageStyles.colDirect}`} aria-hidden="true">{group.directMemberCount > 0 && <Tooltip label={t("groupAdmin.memberBadgeTitle", { count: group.directMemberCount })}><span className={pageStyles.memberBadge}>{group.directMemberCount}</span></Tooltip>}</span>
              <span className={`${pageStyles.valueCell} ${pageStyles.colAllowance}`}><span className={`${pageStyles.allowanceAmount} ${group.monthlyAllowanceUsd === null && getInheritedAllowance(group) !== null ? pageStyles.inheritedAllowance : ""}`}>{group.monthlyAllowanceUsd !== null ? <><span>$</span>{" "}<span>{group.monthlyAllowanceUsd.toFixed(0)}</span></> : getInheritedAllowance(group) !== null ? <><span>$</span>{" "}<span>{getInheritedAllowance(group)!.toFixed(0)}</span></> : null}</span></span>
              <span className={`${pageStyles.valueCell} ${pageStyles.colTotalAllowance}`}>{totalAllowanceText(group.id)}</span>
              {budgetCell("Group", group.id)}
            </button>
          </div>
          {tree.some((child) => child.parentId === group.id) && !collapsedGroupIds.has(group.id) && renderTree(group.id, depth + 1)}
        </li>
      ))}
    </ul>
  );
  // The column titles sit above the rows and share their widths, so each title is above its values at every depth.
  const columnHeader = (
    <div className={pageStyles.treeColumnHeader}>
      <span className={pageStyles.disclosureSpacer} aria-hidden="true" />
      <span className={pageStyles.groupName}>{t("groupAdmin.columns.name")}</span>
      <span className={`${pageStyles.valueCell} ${pageStyles.colTotal}`}>{t("groupAdmin.columns.totalMembers")}</span>
      <span className={`${pageStyles.valueCell} ${pageStyles.colDirect}`}>{t("groupAdmin.columns.directMembers")}</span>
      <span className={`${pageStyles.valueCell} ${pageStyles.colAllowance}`}>{t("groupAdmin.columns.monthlyAllowance")}</span>
      <span className={`${pageStyles.valueCell} ${pageStyles.colTotalAllowance}`}>{t("groupAdmin.columns.totalAllowance")}</span>
      <span className={`${pageStyles.valueCell} ${pageStyles.colCapex}`}>{t("groupAdmin.columns.capex")}</span>
      <span className={`${pageStyles.valueCell} ${pageStyles.colBudget}`}>{t("groupAdmin.columns.budget")}</span>
      {canSeeSpend && <span className={`${pageStyles.valueCell} ${pageStyles.colConsumed}`}>{t("groupAdmin.columns.consumedBudget")}</span>}
    </div>
  );

  return (
    <section className={pageStyles.page}>
      <AdminGate permissions={[permission]} title={t("groupAdmin.deniedTitle")} description={t("groupAdmin.denied")}>
        {loading && <LoadingState label={t("groupAdmin.loading")} />}
        {error && <ErrorState title={t("groupAdmin.errorTitle")} description={error} onRetry={() => void load()} />}
        {notice && <output>{notice}</output>}
        {!loading && !error && (
          <div className={pageStyles.columns}>
            <section ref={treePanelRef} aria-labelledby="group-tree-heading" style={panelStyle}>
              <div className={pageStyles.treeHeader}>
                <h2 id="group-tree-heading">{t("groupAdmin.hierarchy")}</h2>
                <div role="group" aria-label={t("groupAdmin.hierarchy")} className={pageStyles.toolGroup}>
                  <Tooltip label={t("groupAdmin.expandAll")} placement="below" align="end">
                    <button type="button" className={`button button-secondary ${pageStyles.iconButton} ${pageStyles.treeIconButton}`} aria-label={t("groupAdmin.expandAll")} onClick={expandAll}>
                      <svg aria-hidden="true" focusable="false" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="m7 8 5-5 5 5" /><path d="m7 16 5 5 5-5" /></svg>
                    </button>
                  </Tooltip>
                  <Tooltip label={t("groupAdmin.collapseAll")} placement="below" align="end">
                    <button type="button" className={`button button-secondary ${pageStyles.iconButton} ${pageStyles.treeIconButton}`} aria-label={t("groupAdmin.collapseAll")} onClick={collapseAll}>
                      <svg aria-hidden="true" focusable="false" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="m7 4 5 5 5-5" /><path d="m7 20 5-5 5 5" /></svg>
                    </button>
                  </Tooltip>
                  {canImportOrganisation && <Tooltip label={t("organisationImport.openDialog")} placement="below" align="end"><button type="button" className={`button button-secondary ${pageStyles.iconButton}`} aria-label={t("organisationImport.openDialog")} onClick={() => setImportDialogOpen(true)}><BackstageLogo /></button></Tooltip>}
                  {canAdministerEntitlement && <Tooltip label={t("groupAdmin.addDivision")} placement="below" align="end"><button type="button" className={`button button-secondary ${pageStyles.iconButton}`} aria-label={t("groupAdmin.addDivision")} onClick={() => setDivisionDialogOpen(true)}>
                    <svg aria-hidden="true" focusable="false" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 5v14" /><path d="M5 12h14" /></svg>
                  </button></Tooltip>}
                </div>
              </div>
              <div className={pageStyles.treeScroll}>{tree.length === 0 && departments.length === 0 ? <p>{t("groupAdmin.empty")}</p> : <div className={pageStyles.treeTable}>{columnHeader}{renderRoot()}</div>}</div>
            </section>
            {selectedStructure && <EntitlementDetailPanel
              headingId="selected-structure-heading"
              backstageName={selectedStructure.backstageName}
              style={panelStyle}
              footer={canSeeAllGroups ? <div className={pageStyles.panelFooter}>
                {structureDeletable && renderDelete(selectedStructure.id, () => deleteStructure())}
                {selectedStructure.kind === "Division" && <button type="button" className="button button-primary" onClick={() => openCreate("Department", selectedStructure.id)}>{t("groupAdmin.addDepartment")}</button>}
                {selectedStructure.kind === "Department" && <button type="button" className="button button-primary" onClick={() => openCreate("Group", selectedStructure.id)}>{t("groupAdmin.addGroup")}</button>}
              </div> : undefined}
              heading={<h2 id="selected-structure-heading" className={pageStyles.groupNameHeading}>
                {canSeeAllGroups ? <StructureName key={selectedStructure.id} node={selectedStructure} onRenamed={() => load(false)} /> : selectedStructure.value}
              </h2>}
            >
              <DetailRow label={t("groupAdmin.structure.ownerLabel")}>
                <GroupOwnerValue
                  key={selectedStructure.id}
                  managerEmail={selectedStructure.managerEmail ?? null}
                  editable={canSeeAllGroups && !selectedStructure.backstageName}
                  save={(personId) => setAssociationOwner(selectedStructure.kind as ManagedAssociationKind, selectedStructure.id, personId)}
                  onSaved={() => load(false)}
                />
              </DetailRow>
              <DetailRow label={t("groupAdmin.parent")} applies={selectedStructure.kind === "Department"}>{divisions.find((division) => division.id === selectedStructure.parentId)?.value}</DetailRow>
              <DetailRow label={t("groupAdmin.columns.monthlyAllowance")} applies={false} />
              <DetailRow label={t("groupAdmin.structure.totalAllowanceLabel")}>
                {structureTotalAllowanceUsd !== null && t("groupAdmin.structure.totalAllowanceValue", { amount: wholeDollars.format(structureTotalAllowanceUsd) })}
              </DetailRow>
              <DetailRow label={t("budgets.label")}>
                {structureBudget && <BudgetEditor key={budgetEditorKey(structureBudget)} unit={structureBudget} editable={canSeeAllGroups} canBackdate={canBackdate} onSaved={() => void load(false)} />}
              </DetailRow>
              <DetailRow label={t("budgets.history.title")}>
                {structureBudget && <BudgetHistory key={budgetEditorKey(structureBudget)} kind={structureBudget.kind} id={structureBudget.id} />}
              </DetailRow>
              <DetailRow label={t("budgets.allowanceHistory.title")} applies={false} />
              <DetailRow label={t("groupAdmin.members")}>
                {structureUserCount !== null && <>
                  <span className={pageStyles.memberBadge} data-testid="structure-direct-count">{t("groupAdmin.directCount", { count: structureDirectCount ?? 0 })}</span>
                  <span className={pageStyles.totalMemberBadge} data-testid="structure-user-count">{t("groupAdmin.totalCount", { count: structureUserCount })}</span>
                  {structureUserCount > 0 && <Tooltip label={t("groupAdmin.structure.showEmails")}><button type="button" className={pageStyles.emailBadge} aria-label={t("groupAdmin.structure.showEmails")} onClick={() => setStructureEmailsOpen(true)}>
  <svg aria-hidden="true" focusable="false" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="4" width="20" height="16" rx="2" /><path d="m22 7-10 6L2 7" /></svg></button></Tooltip>}
                </>}
                {selectedStructure.kind === "Department" && canAdministerEntitlement && canSeeAllGroups && <Tooltip label={t("admin.associationValues.assignUsers")}><button type="button" className={`button button-secondary ${pageStyles.iconButton} ${pageStyles.assignUsersButton}`} aria-label={t("admin.associationValues.assignUsers")} onClick={() => setAssignUsersOpen(true)}>
                    <svg aria-hidden="true" focusable="false" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="9" cy="8" r="4" /><path d="M2 21v-1a6 6 0 0 1 6-6h2a6 6 0 0 1 6 6v1" /><path d="M19 8v6" /><path d="M16 11h6" /></svg>
                  </button></Tooltip>}
              </DetailRow>
              <DetailRow label="" applies={false} />

            </EntitlementDetailPanel>}
            {selected?.canManage === false && <ReadonlyGroupPanel group={selected} parentName={groups.find((group) => group.id === selected.parentId)?.name ?? departments.find((department) => department.id === selected.departmentId)?.value ?? null} style={panelStyle} />}
            {selected && selected.canManage !== false && <EntitlementDetailPanel
              headingId="selected-group-heading"
              backstageName={selected.backstageName}
              style={panelStyle}
              heading={editingName ? <form className={`${styles.form} ${pageStyles.nameForm}`} onSubmit={(event) => void rename(event)}>
                <input aria-label={t("groupAdmin.name")} id="group-rename" name="rename" defaultValue={selected.name} required maxLength={120} autoFocus />
                <button type="submit" className="button button-primary" disabled={busy || Boolean(selected.archivedAt)}>{t("groupAdmin.rename")}</button>
                <button type="button" className="button button-secondary" onClick={() => setEditingName(false)} disabled={busy}>{t("groupAdmin.cancel")}</button>
              </form> : <h2 id="selected-group-heading" className={pageStyles.groupNameHeading}>
                <button type="button" className={pageStyles.nameTextButton} onClick={() => setEditingName(true)} disabled={busy || Boolean(selected.archivedAt)}>{selected.name}</button>
              </h2>}
              footer={<>
                {selected.archivedAt && <p>{t("groupAdmin.archivedStatus")}</p>}
                {preview && <section aria-labelledby="impact-heading" className={pageStyles.preview}>
                  <h3 id="impact-heading">{t("groupAdmin.impact", { count: preview.users.length })}</h3>
                  <p>{t("groupAdmin.affectedGroups", { count: preview.affectedGroupIds.length })}</p>
                  <div className={pageStyles.impactTableWrapper}>
                    <table className={pageStyles.impactTable}>
                      <thead><tr><th scope="col">{t("groupAdmin.userColumn")}</th><th scope="col">{t("groupAdmin.currentAllowanceColumn")}</th><th scope="col">{t("groupAdmin.newAllowanceColumn")}</th></tr></thead>
                      <tbody>{preview.users.map((user) => <tr key={user.personId}><th scope="row">{user.email}</th><td>${user.beforeUsd.toFixed(2)}</td><td>${user.afterUsd.toFixed(2)}</td></tr>)}</tbody>
                    </table>
                  </div>
                  <div className={pageStyles.previewActions}>
                    <button type="button" className="button button-primary" onClick={() => void confirmChange()} disabled={busy}>{t("groupAdmin.confirm")}</button>
                    <button type="button" className="button button-secondary" onClick={() => setPreview(null)} disabled={busy}>{t("groupAdmin.cancel")}</button>
                  </div>
                </section>}
                <div className={pageStyles.panelFooter}>
                  {groupDeletable && renderDelete(selected.id, () => deleteGroup())}
                  <button type="button" className="button button-primary" onClick={() => openCreate("Group", selected.id)} disabled={busy || Boolean(selected.archivedAt)}>{t("groupAdmin.addGroup")}</button>
                </div>
              </>}
            >
              <DetailRow label={t("groupAdmin.structure.ownerLabel")}>
                <GroupOwnerValue
                  key={selected.id}
                  managerEmail={selected.managerEmail ?? null}
                  editable={!selected.backstageName}
                  save={(personId) => setEntitlementGroupOwner(selected.id, personId)}
                  onSaved={() => load(false)}
                />
              </DetailRow>
              <DetailRow label={t("groupAdmin.parent")}>
                {selected.backstageName
                  ? <span>{parentText(selected)}</span>
                  : <div className={pageStyles.parentField}>
                    <Dropdown
                      id="group-parent"
                      ariaLabel={t("groupAdmin.parent")}
                      options={[{ value: "", label: t("groupAdmin.parentRequired"), disabled: true }, ...parentOptions(selected.id)]}
                      value={draftParent}
                      onChange={(next) => { setDraftParent(next); setPreview(null); }}
                    />
                  </div>}
              </DetailRow>
              <DetailRow label={t("groupAdmin.columns.monthlyAllowance")}>
                <div className={pageStyles.allowanceField}>
                  <div className={pageStyles.currencyInput}>
                    <span className={pageStyles.currencySymbol} aria-hidden="true">$</span>
                    <input
                      id="group-allowance"
                      aria-label={t("groupAdmin.allowance")}
                      type="number"
                      min="0"
                      max="99999"
                      step="1"
                      value={draftAllowance}
                      placeholder={selected.monthlyAllowanceUsd === null ? (getInheritedAllowance(selected)?.toFixed(0) ?? undefined) : undefined}
                      onChange={(event) => { setDraftAllowance(event.target.value); setPreview(null); }}
                      onKeyDown={(event) => { if (event.key === "Enter" && groupChangeDirty) { event.preventDefault(); void requestPreview(); } }}
                    />
                  </div>
                </div>
                {groupChangeDirty && <button type="button" className="button button-primary" onClick={() => void requestPreview()} disabled={busy || Boolean(selected.archivedAt)}>{t("groupAdmin.save")}</button>}
              </DetailRow>
              <DetailRow label={t("groupAdmin.structure.totalAllowanceLabel")}>
                {groupTotalAllowanceUsd !== null && t("groupAdmin.structure.totalAllowanceValue", { amount: wholeDollars.format(groupTotalAllowanceUsd) })}
              </DetailRow>
              <DetailRow label={t("budgets.label")}>
                {selectedBudget && <BudgetEditor key={budgetEditorKey(selectedBudget)} unit={selectedBudget} editable canBackdate={canBackdate} onSaved={() => void load(false)} />}
              </DetailRow>
              <DetailRow label={t("budgets.history.title")}>
                {selectedBudget && <BudgetHistory key={budgetEditorKey(selectedBudget)} kind="Group" id={selectedBudget.id} />}
              </DetailRow>
              <DetailRow label={t("budgets.allowanceHistory.title")}>
                <GroupAllowanceHistory key={`${selected.id}:${String(selected.monthlyAllowanceUsd)}`} groupId={selected.id} />
              </DetailRow>
              <MembershipForm
                key={selected.id}
                groupId={selected.id}
                members={members}
                membersLoading={membersLoading}
                directCount={selected.directMemberCount}
                totalCount={totalMembersByGroup.get(selected.id) ?? selected.directMemberCount}
                busy={busy}
                onBusy={setBusy}
                onError={setError}
                onNotice={setNotice}
                onReload={async () => { await load(false); await loadMembers(selected.id); }}
                onBulkAdd={() => setBulkDialogOpen(true)}
                onExport={() => setExportDialogOpen(true)}
              />
            </EntitlementDetailPanel>}
          </div>
        )}
        {createDialogOpen && <Modal title={createKind === "Group" ? t("groupAdmin.create") : t("groupAdmin.createDepartment")} onClose={() => setCreateDialogOpen(false)} showClose={false}>
          <form className={`${styles.form} ${pageStyles.createForm}`} onSubmit={(event) => void create(event)}>
            <div className={styles.field}><label htmlFor="new-group-name">{t("groupAdmin.name")}</label><input id="new-group-name" name="name" required maxLength={120} data-autofocus /></div>
                  {createKind === "Group" && <div className={[styles.field, pageStyles.allowanceField].join(" ")}><label htmlFor="new-group-allowance">{t("groupAdmin.allowance")}</label>
                  <div className={pageStyles.currencyInput}>
                    <span className={pageStyles.currencySymbol} aria-hidden="true">$</span>
                    <input id="new-group-allowance" name="allowance" type="number" min="0" max="99999" step="1" />
                  </div>
                </div>}
            <div className={pageStyles.dialogActions}>
              <button type="button" className="button button-secondary" onClick={() => setCreateDialogOpen(false)} disabled={busy}>{t("groupAdmin.cancel")}</button>
              <button type="submit" className="button button-primary" disabled={busy}>{createKind === "Group" ? t("groupAdmin.create") : t("groupAdmin.createDepartment")}</button>
            </div>
          </form>
        </Modal>}
        {bulkDialogOpen && selected && <Modal title={t("groupAdmin.bulkTitle")} onClose={() => setBulkDialogOpen(false)}>
          <BulkForm group={selected} busy={busy} onBusy={setBusy} onError={setError} onNotice={setNotice} onReload={() => { void load(false); void loadMembers(selected.id); }} />
        </Modal>}
        {exportDialogOpen && selected && <Modal title={t("groupAdmin.exportTitle")} onClose={() => setExportDialogOpen(false)}>
          <ExportForm emails={members.map((member) => member.email)} />
        </Modal>}
        {structureEmailsOpen && selectedStructure && structureUsers?.id === selectedStructure.id && <Modal title={t("groupAdmin.structure.emailsTitle", { value: selectedStructure.value })} onClose={() => setStructureEmailsOpen(false)}>
          <ExportForm emails={structureUsers.emails} />
        </Modal>}
        {importDialogOpen && canImportOrganisation && <OrganisationImportDialog onClose={() => setImportDialogOpen(false)} onApplied={() => void load(false)} />}
        {divisionDialogOpen && <Modal title={t("groupAdmin.addDivision")} onClose={() => setDivisionDialogOpen(false)} showClose={false}>
          <form className={`${styles.form} ${pageStyles.createForm}`} onSubmit={(event) => void createDivision(event)}>
            <p>{t("groupAdmin.addDivisionExplanation")}</p>
            <div className={styles.field}><label htmlFor="new-division-name">{t("groupAdmin.name")}</label><input id="new-division-name" name="name" required maxLength={120} data-autofocus /></div>
            <div className={pageStyles.dialogActions}>
              <button type="button" className="button button-secondary" onClick={() => setDivisionDialogOpen(false)} disabled={busy}>{t("groupAdmin.cancel")}</button>
              <button type="submit" className="button button-primary" disabled={busy}>{t("groupAdmin.createDivision")}</button>
            </div>
          </form>
        </Modal>}
        {assignUsersOpen && selectedStructure?.kind === "Department" && <Modal
          title={t("admin.associationValues.assignUsersTitle", { kind: t("admin.associationValues.kinds.department"), value: selectedStructure.value })}
          onClose={() => setAssignUsersOpen(false)}
          closeOnBackdropClick={false}
          closeOnEscape={false}
        >
          <BulkAssociationSection kind="Department" value={selectedStructure.value} onComplete={async () => { setStructureUsersRevision((revision) => revision + 1); await load(false); }} />
        </Modal>}
      </AdminGate>
    </section>
  );
}

/**
 * The name of a Division or Department in the detail panel. It is a button until clicked, then an input that saves on blur; Enter
 * leaves the field and Escape puts the old name back. An imported unit's name is text, because Backstage owns it.
 */
function StructureName({ node, onRenamed }: { node: AssociationValue; onRenamed: () => Promise<void> }): React.JSX.Element {
  const { t } = useTranslation();
  const toast = useToast();
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);

  const save = async (value: string) => {
    const name = value.trim();
    setEditing(false);
    if (name === node.value) return;
    if (!name) {
      toast.show(t("groupAdmin.structure.nameRequired"), "error");
      return;
    }

    setSaving(true);
    const result = await renameAssociationValue(node.kind as ManagedAssociationKind, node.id, name, node.parentId ?? undefined);
    setSaving(false);
    if (result.kind === "ok") {
      toast.show(t("groupAdmin.structure.renamed"), "success");
      await onRenamed();
      return;
    }
    toast.show(result.kind === "denied" ? t("groupAdmin.denied") : result.message, "error");
  };

  if (node.backstageName) return <>{node.value}</>;
  if (!editing) return <button type="button" className={pageStyles.nameTextButton} onClick={() => setEditing(true)} disabled={saving}>{node.value}</button>;
  return <span className={pageStyles.nameForm}>
    <input
      aria-label={t("groupAdmin.name")}
      defaultValue={node.value}
      maxLength={120}
      autoFocus
      onBlur={(event) => void save(event.currentTarget.value)}
      onKeyDown={(event) => {
        if (event.key === "Enter") event.currentTarget.blur();
        if (event.key === "Escape") {
          event.currentTarget.value = node.value;
          event.currentTarget.blur();
        }
      }}
    />
  </span>;
}

/** How a person appears as a search match: the name with the email beside it, or the email alone when there is no name. */
function personOptionLabel(person: SearchPersonSummary): string {
  return person.displayName.trim() ? `${person.displayName} (${person.email})` : person.email;
}

/**
 * The people an administrator may search for. A refusal becomes the group admin denied message. A person `exclude` returns true for is
 * left out of the matches, such as someone who is already a member.
 */
function usePersonSearch(exclude: (person: SearchPersonSummary) => boolean = () => false): (term: string) => Promise<SearchSelectOutcome<SearchPersonSummary>> {
  const { t } = useTranslation();
  return useCallback(async (term: string) => {
    // The people list is the Users page's own call: it needs entitlement.administer, which the impersonation search does not.
    const result = await fetchPeople(undefined, term.trim() || undefined, 0, 10);
    if (result.kind === "ok") return { kind: "ok", items: result.response.items.filter((person) => !exclude(person)) };
    return { kind: "error", message: result.kind === "denied" ? t("groupAdmin.searchDenied", { permission: t("admin.roles.permissionCatalog.entitlementAdminister.name") }) : result.message };
  }, [t, exclude]);
}

/**
 * The group owner value of a group, Division or Department. An imported unit shows its owner as text. Any other unit shows the owner
 * as a button; clicking it turns the value into a person search, and choosing a person saves them as the owner straight away.
 */
function GroupOwnerValue({ managerEmail, editable, save, onSaved }: {
  managerEmail: string | null;
  editable: boolean;
  save: (personId: string | null) => Promise<AdminResult<UnitOwnerResponse>>;
  onSaved: () => Promise<void>;
}): React.JSX.Element {
  const { t } = useTranslation();
  const toast = useToast();
  const [editing, setEditing] = useState(false);
  const [search, setSearch] = useState("");
  const [saving, setSaving] = useState(false);
  const [confirmingRemoval, setConfirmingRemoval] = useState(false);
  const searchPeople = usePersonSearch();
  const ownerLabel = managerEmail ?? t("groupAdmin.structure.noGroupOwner");

  const choose = async (person: SearchPersonSummary) => {
    setSaving(true);
    const result = await save(person.id);
    setSaving(false);
    if (result.kind === "ok") {
      toast.show(t("groupAdmin.owner.saved"), "success");
      setEditing(false);
      setSearch("");
      await onSaved();
      return;
    }
    toast.show(result.kind === "denied" ? t("groupAdmin.denied") : result.message, "error");
  };

  // Removing the owner is a second step: the × asks, and Confirm saves. The change is then set back to none and audited.
  const removeOwner = async () => {
    setConfirmingRemoval(false);
    setSaving(true);
    const result = await save(null);
    setSaving(false);
    if (result.kind === "ok") {
      toast.show(t("groupAdmin.owner.removed"), "success");
      await onSaved();
      return;
    }
    toast.show(result.kind === "denied" ? t("groupAdmin.denied") : result.message, "error");
  };

  const ownerClass = managerEmail ? undefined : pageStyles.ownerEmpty;
  if (!editable) return <span className={ownerClass}>{ownerLabel}</span>;
  if (!editing) return <span className={pageStyles.ownerView}>
    <button type="button" className={`${pageStyles.ownerButton} ${ownerClass ?? ""}`} onClick={() => setEditing(true)}>{ownerLabel}</button>
    {managerEmail && !confirmingRemoval && <Tooltip label={t("groupAdmin.owner.remove")}><button type="button" className={pageStyles.fieldButton} aria-label={t("groupAdmin.owner.remove")} onClick={() => setConfirmingRemoval(true)} disabled={saving}>×</button></Tooltip>}
    {managerEmail && confirmingRemoval && <>
      <button type="button" className="button button-primary" onClick={() => void removeOwner()} disabled={saving}>{t("groupAdmin.owner.confirmRemove")}</button>
      <Tooltip label={t("searchSelect.cancel")}><button type="button" className={pageStyles.fieldButton} aria-label={t("searchSelect.cancel")} onClick={() => setConfirmingRemoval(false)} disabled={saving}>×</button></Tooltip>
    </>}
  </span>;
  return <div className={pageStyles.ownerEditor}>
    <SearchSelect
      label={t("groupAdmin.owner.searchLabel")}
      placeholder={t("groupAdmin.addMemberPlaceholder")}
      value={search}
      onValueChange={setSearch}
      search={searchPeople}
      itemKey={(person) => person.id}
      itemLabel={personOptionLabel}
      onSelect={(person) => void choose(person)}
      onEscape={() => setEditing(false)}
      disabled={saving}
      autoFocus
      className={pageStyles.searchField}
      actions={<Tooltip label={t("searchSelect.cancel")}><button type="button" className={pageStyles.fieldButton} aria-label={t("searchSelect.cancel")} onClick={() => setEditing(false)} disabled={saving}>×</button></Tooltip>}
    />
  </div>;
}

/**
 * The three rows that hold a group's membership: its members with their counts and the email list, the member list itself, and the
 * add-member search. They render inside the group's detail panel, after its allowance rows, so the same panel layout holds for a Division.
 */
function MembershipForm({ groupId, members, membersLoading, directCount, totalCount, busy, onBusy, onError, onNotice, onReload, onBulkAdd, onExport }: {
  groupId: string; members: EntitlementGroupMember[]; membersLoading: boolean; directCount: number; totalCount: number; busy: boolean; onBusy: (value: boolean) => void; onError: (value: string | null) => void; onNotice: (value: string | null) => void; onReload: () => Promise<void>; onBulkAdd: () => void; onExport: () => void;
}): React.JSX.Element {
  const { t } = useTranslation();
  const toast = useToast();
  const [search, setSearch] = useState("");
  const [selectedPerson, setSelectedPerson] = useState<SearchPersonSummary | null>(null);
  const [preview, setPreview] = useState<{ details: GroupImpactPreview; personId: string; add: boolean } | null>(null);
  const searchPeople = usePersonSearch((person) => members.some((member) => member.personId === person.id));

  const request = async (personId: string, add: boolean) => {
    setPreview(null); onError(null); onBusy(true);
    const result = await previewEntitlementMembership(groupId, personId, add);
    onBusy(false);
    if (result.kind === "ok") setPreview({ details: result.response, personId, add });
    else onError(result.kind === "denied" ? t("groupAdmin.denied") : result.message);
  };

  const confirm = async () => {
    if (!preview) return;
    const snapshot = preview;
    setPreview(null); onBusy(true); onError(null);
    const result = await confirmEntitlementMembership(groupId, snapshot.personId, snapshot.add, snapshot.details.revisionSnapshot);
    onBusy(false);
    if (result.kind === "ok") {
      toast.show(t("groupAdmin.membershipSaved"), "success"); setSearch(""); setSelectedPerson(null);
      await onReload();
    } else {
      onError(result.kind === "denied" ? t("groupAdmin.denied") : result.message);
      onNotice(result.kind === "conflict" ? t("groupAdmin.conflictReloaded") : t("groupAdmin.auditMayHaveCommitted"));
      await onReload();
    }
  };

  return <>
    <DetailRow label={t("groupAdmin.members")}>
      <Tooltip label={t("groupAdmin.memberBadgeTitle", { count: directCount })}><span className={pageStyles.memberBadge} data-testid="member-count">{t("groupAdmin.directCount", { count: directCount })}</span></Tooltip>
      <Tooltip label={t("groupAdmin.totalMemberBadgeTitle", { count: totalCount })}><span className={pageStyles.totalMemberBadge}>{t("groupAdmin.totalCount", { count: totalCount })}</span></Tooltip>
      <Tooltip label={t("groupAdmin.exportTitle")}>
        <button type="button" className={pageStyles.emailBadge} aria-label={t("groupAdmin.exportTitle")} onClick={onExport} disabled={busy}>
  <svg aria-hidden="true" focusable="false" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="4" width="20" height="16" rx="2" /><path d="m22 7-10 6L2 7" /></svg></button></Tooltip>
    </DetailRow>
    <DetailRow label="">
      {membersLoading && <LoadingState label={t("groupAdmin.membersLoading")} />}
      {!membersLoading && members.length === 0 && <p>{t("groupAdmin.noMembers")}</p>}
      {members.length > 0 && <ul className={pageStyles.memberList}>{members.map((member) => <li key={member.membershipId} className={member.contributesToAllowance ? undefined : pageStyles.memberNotContributing}><Tooltip label={member.displayName ? member.email : undefined}>{member.displayName || member.email}</Tooltip><Tooltip label={t("groupAdmin.removeUserTitle")}><button type="button" className={pageStyles.removeMemberButton} aria-label={t("groupAdmin.removeUser", { name: member.displayName || member.email })} onClick={() => void request(member.personId, false)} disabled={busy}>×</button></Tooltip></li>)}</ul>}
    </DetailRow>
    <DetailRow label={t("groupAdmin.addMember")}>
      <div className={pageStyles.memberSearch}>
          <SearchSelect
            label={t("groupAdmin.addMember")}
            placeholder={t("groupAdmin.addMemberPlaceholder")}
            value={search}
            onValueChange={(value) => { setSearch(value); setSelectedPerson(null); setPreview(null); }}
            search={searchPeople}
            itemKey={(person) => person.id}
            itemLabel={personOptionLabel}
            onSelect={(person) => { setSelectedPerson(person); setSearch(personOptionLabel(person)); }}
            className={pageStyles.searchField}
            actions={<>
              <Tooltip label={t("searchSelect.cancel")}><button type="button" className={pageStyles.fieldButton} aria-label={t("searchSelect.cancel")} onClick={() => { setSearch(""); setSelectedPerson(null); setPreview(null); }} disabled={busy}>×</button></Tooltip>
              <Tooltip label={t("groupAdmin.bulkTitle")}>
                <button type="button" className={pageStyles.fieldButton} aria-label={t("groupAdmin.bulkTitle")} onClick={onBulkAdd} disabled={busy}>
                  <svg aria-hidden="true" focusable="false" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="9" cy="8" r="4" /><path d="M2 21v-1a6 6 0 0 1 6-6h2a6 6 0 0 1 6 6v1" /><path d="M19 8v6" /><path d="M16 11h6" /></svg>
                </button>
              </Tooltip>
            </>}
          />
        {selectedPerson && <button type="button" className="button button-primary" onClick={() => void request(selectedPerson.id, true)} disabled={busy}>{t("groupAdmin.addUser")}</button>}
      </div>
      {preview && <fieldset className={`${pageStyles.preview} ${pageStyles.membershipPreview}`}><p>{t("groupAdmin.membershipImpact", { count: preview.details.users.length })}</p><div className={pageStyles.impactTableWrapper}><table className={pageStyles.impactTable}><thead><tr><th scope="col">{t("groupAdmin.userColumn")}</th><th scope="col">{t("groupAdmin.currentAllowanceColumn")}</th><th scope="col">{t("groupAdmin.newAllowanceColumn")}</th></tr></thead><tbody>{preview.details.users.map((user) => <tr key={user.personId}><th scope="row">{user.email}</th><td>${user.beforeUsd.toFixed(2)}</td><td>${user.afterUsd.toFixed(2)}</td></tr>)}</tbody></table></div><div className={pageStyles.previewActions}><button type="button" className="button button-primary" onClick={() => void confirm()} disabled={busy}>{t("groupAdmin.confirm")}</button><button type="button" className="button button-secondary" onClick={() => setPreview(null)} disabled={busy}>{t("groupAdmin.cancel")}</button></div></fieldset>}
    </DetailRow>
  </>;
}

function ExportForm({ emails }: { emails: string[] }): React.JSX.Element {
  const { t } = useTranslation();
  const [copied, setCopied] = useState(false);
  const text = [...emails].sort((left, right) => left.localeCompare(right)).join(",");
  const copy = async () => {
    await navigator.clipboard.writeText(text);
    setCopied(true);
  };
  if (emails.length === 0) return <p>{t("groupAdmin.exportEmpty")}</p>;
  return (
    <div className={styles.form}>
      <p>{t("groupAdmin.exportHint")}</p>
      <div className={`${styles.field} ${styles.descriptionField} ${pageStyles.bulkEmailsField}`}>
        <label htmlFor="group-export-emails">{t("groupAdmin.exportEmails")}</label>
        <textarea className={pageStyles.bulkEmailsTextarea} id="group-export-emails" value={text} readOnly rows={10} data-autofocus onFocus={(event) => event.currentTarget.select()} />
      </div>
      <button type="button" className="button button-primary" onClick={() => void copy()}>{t("groupAdmin.exportCopy")}</button>
      {copied && <p role="status">{t("groupAdmin.exportCopied")}</p>}
    </div>
  );
}

function BulkForm({ group, busy, onBusy, onError, onNotice, onReload }: {
  group: EntitlementGroupSummary; busy: boolean; onBusy: (value: boolean) => void; onError: (value: string | null) => void; onNotice: (value: string | null) => void; onReload: () => void;
}): React.JSX.Element {
  const { t } = useTranslation();
  const toast = useToast();
  const [emails, setEmails] = useState("");
  const [outcomes, setOutcomes] = useState<BulkMembershipOutcome[]>([]);
  const submit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const values = emails.split(/[\s,;]+/).map((email) => email.trim()).filter(Boolean);
    if (values.length > 1000) { onError(t("groupAdmin.bulkLimit")); return; }
    onBusy(true); onError(null); setOutcomes([]);
    const result = await bulkAddEntitlementGroupMembers(group.id, group.revision, values);
    onBusy(false);
    if (result.kind === "ok") {
      setOutcomes(result.response); toast.show(t("groupAdmin.bulkComplete", { added: result.response.filter((item) => item.status === "added").length, total: result.response.length }), "success"); onReload();
    } else {
      onError(result.kind === "denied" ? t("groupAdmin.denied") : result.message);
      onNotice(result.kind === "conflict" ? t("groupAdmin.conflictReloaded") : t("groupAdmin.auditMayHaveCommitted")); onReload();
    }
  };
  return <div>
    <form className={`${styles.form} ${pageStyles.bulkForm}`} onSubmit={(event) => void submit(event)}>
      <div className={`${styles.field} ${styles.descriptionField} ${pageStyles.bulkEmailsField}`}><label htmlFor="group-bulk-emails">{t("groupAdmin.bulkEmails")}</label><textarea className={pageStyles.bulkEmailsTextarea} id="group-bulk-emails" value={emails} onChange={(event) => setEmails(event.target.value)} rows={16} /></div>
      <button type="submit" className="button button-primary" disabled={busy || !emails.trim()}>{t("groupAdmin.assignUsers")}</button>
    </form>
    {outcomes.length > 0 && <ul aria-label={t("groupAdmin.bulkResults")}>{outcomes.map((outcome, index) => <li key={`${outcome.email}-${index}`}>{outcome.email}: {t(`groupAdmin.outcomes.${outcome.status}`, { defaultValue: outcome.status })}</li>)}</ul>}
  </div>;
}

/**
 * A parent group the caller may see but not change: its owner, its parent and the members the caller may see. It has no controls, and
 * its members are not loaded, because the server would refuse them.
 */
function ReadonlyGroupPanel({ group, parentName, style }: { group: EntitlementGroupSummary; parentName: string | null; style: React.CSSProperties | undefined }): React.JSX.Element {
  const { t } = useTranslation();
  return (
    <EntitlementDetailPanel
      headingId="selected-group-heading"
      backstageName={group.backstageName}
      style={style}
      heading={<h2 id="selected-group-heading" className={pageStyles.groupNameHeading}>{group.name}</h2>}
    >
      <DetailRow label={t("groupAdmin.structure.ownerLabel")}>{group.managerEmail ?? "—"}</DetailRow>
      <DetailRow label={t("groupAdmin.parent")}>{parentName ?? "—"}</DetailRow>
      <DetailRow label={t("groupAdmin.members")}>
        <span className={pageStyles.memberBadge} data-testid="readonly-member-count">{t("groupAdmin.directCount", { count: group.directMemberCount })}</span>
      </DetailRow>
    </EntitlementDetailPanel>
  );
}
